import React, { useMemo, useState } from "react";

/** Build an embed URL from lat/lng/zoom (no API key required) */
function mapEmbedUrl(lat, lng, zoom = 15) {
  if (!isFinite(lat) || !isFinite(lng)) return "";
  const z = Math.min(21, Math.max(1, Math.round(zoom)));
  return `https://www.google.com/maps?q=${lat},${lng}&z=${z}&output=embed`;
}

export default function Location() {
  // Defaults from your original embed
  const [lat, setLat] = useState(-4.184307039660235);
  const [lng, setLng] = useState(121.6118008473256);
  const [zoom, setZoom] = useState(17);

  // Apply on button click
  const [applied, setApplied] = useState({ lat, lng, zoom });

  const src = useMemo(
    () => mapEmbedUrl(applied.lat, applied.lng, applied.zoom),
    [applied]
  );

  const valid =
    isFinite(lat) && isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <h2 className="text-3xl font-semibold text-gray-800">RD4 Location</h2>

        {/* Controls card */}
        <div className="bg-white rounded-lg shadow p-4">
          {/* Row 1 — inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-4">
              <label className="mb-1 block text-sm font-medium text-gray-600">Latitude</label>
              <input
                type="number"
                step="any"
                inputMode="decimal"
                lang="en"
                value={lat}
                onChange={(e) => setLat(parseFloat(e.target.value))}
                placeholder="-4.1843"
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm shadow-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="lg:col-span-4">
              <label className="mb-1 block text-sm font-medium text-gray-600">Longitude</label>
              <input
                type="number"
                step="any"
                inputMode="decimal"
                lang="en"
                value={lng}
                onChange={(e) => setLng(parseFloat(e.target.value))}
                placeholder="121.6118"
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm shadow-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="lg:col-span-4">
              <label className="mb-1 block text-sm font-medium text-gray-600">Zoom (1–21)</label>
              <input
                type="number"
                min={1}
                max={21}
                value={zoom}
                onChange={(e) => setZoom(parseInt(e.target.value || "15", 10))}
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm shadow-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Row 2 — actions, full width at bottom */}
            <div className="lg:col-span-12 flex justify-start gap-3 pt-1">
              <button
                onClick={() => setApplied({ lat, lng, zoom })}
                disabled={!valid}
                className="inline-flex h-10 items-center whitespace-nowrap rounded-md bg-blue-600 px-3 text-sm
                           font-medium text-white shadow-sm hover:bg-blue-700
                           focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update map
              </button>

              <a
                href={`https://www.google.com/maps/search/?api=1&query=${applied.lat},${applied.lng}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 items-center whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 text-sm
                           font-medium text-gray-700 shadow-sm hover:bg-gray-50
                           focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              >
                Open in Google&nbsp;Maps
              </a>
            </div>
          </div>

          {!valid && (
            <p className="mt-3 text-sm text-red-600">
              Latitude must be between −90 and 90, longitude between −180 and 180.
            </p>
          )}
        </div>

        {/* Map */}
        <div className="bg-white rounded-lg shadow p-2">
          {src ? (
            <iframe
              title="location-map"
              src={src}
              className="w-full h-[520px] rounded-md"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          ) : (
            <div className="h-[520px] grid place-items-center text-gray-500 text-sm">
              Enter a valid latitude & longitude, then click{" "}
              <span className="font-medium ml-1">Update map</span>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
